import { useEffect, useMemo, useRef, useState } from "react";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import HeartBrokenIcon from "@mui/icons-material/HeartBroken";
import LanguageIcon from "@mui/icons-material/Language";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  Badge,
  CircularProgress,
  Collapse,
  Grid,
  Tab,
  Tabs,
  Tooltip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import DefaultInput from "@/components/forms/inputs/defaultInput";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import DropDown from "@/components/forms/inputs/dropDown";
import RoundedContentBox from "@/components/general/roundedContentBox";
import LmsCourseCard, {
  LmsCourseCardData,
} from "@/components/lms/lmsCourseCard";
import {
  getManagerCourseListQueryParameters,
  getAvailableDurationsLabel,
  getAvailableLanguagesLabel,
  loadPaginatedEntity,
  ManagerCourseTab,
  mapManagerCourseToCardData,
} from "@/components/lms/lmsCoursesShared";
import { useLmsCourseListControls } from "@/components/lms/useLmsCourseListControls";
import {
  COURSE_CREATE_PERMISSION,
  COURSE_UPDATE_PERMISSION,
  getEffectiveLmsMode,
  getLmsCourseLanguageOptions,
  getLmsCourseOverviewRoute,
  getLmsCoursesRoute,
  getLmsCreateCourseRoute,
  getLmsManagerRequestConfig,
  getLmsManagerRequestParameters,
  loadLmsCourseCategoryOptions,
  LmsMode,
  shouldBlockLmsForSubdomain,
} from "@/components/lms/lms.utils";
import { RootState } from "@/components/state/store";
import GenericCrudTable from "@/components/tables/genericCrudTable";
import type { CrudColumnDef } from "@/components/tables/crudColumnDef";
import { getRecordMetadataColumns } from "@/components/tables/recordMetadataColumns";
import Restricted from "@/consumer/restricted";
import styles from "@/routes/lms/lmsAdminCoursesView.module.css";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import { replaceDate } from "@/helper/dateHandling";
import { useManagerLmsAccess } from "@/context/managerLmsAccessContext";
import LmsMarketingBanner from "@/components/lms/lmsMarketingBanner";
import LmsBookNowModal from "@/components/lms/lmsBookNowModal";

interface Props {
  requiredMode: Extract<LmsMode, "administrator" | "manager">;
}

interface FilterOption {
  value: string;
  label: string;
}

interface ManagerFilters {
  category: FilterOption | null;
  language: FilterOption | null;
}

const initialManagerFilters: ManagerFilters = {
  category: null,
  language: null,
};

export default function LmsAdminCoursesView({ requiredMode }: Props) {
  const { t } = useTranslation();
  usePageHeading(t("course_library"));
  const navigate = useNavigate();
  const loginState = useSelector((state: RootState) => state.login);
  const selectedChildCompany = useSelector((state: RootState) => state.parent);
  const lmsAccess = useManagerLmsAccess();
  const isMarketingMode =
    requiredMode === "manager" && lmsAccess.mode === "marketing";

  const {
    activeTab: managerTab,
    appliedSearchTerm,
    filters: managerFilters,
    isFiltersOpen,
    searchInput,
    setActiveTab: setManagerTab,
    setFilters: setManagerFilters,
    setIsFiltersOpen,
    setSearchInput,
  } = useLmsCourseListControls<ManagerCourseTab, ManagerFilters>({
    initialFilters: initialManagerFilters,
    initialTab: isMarketingMode ? "all" : "enrolled",
  });
  const hasUserSelectedManagerTab = useRef(false);
  const [categoryOptions, setCategoryOptions] = useState<FilterOption[]>([]);
  const [managerCoursesByTab, setManagerCoursesByTab] = useState<
    Record<ManagerCourseTab, LmsCourseCardData[]>
  >({
    enrolled: [],
    all: [],
  });
  const [managerTotalCountByTab, setManagerTotalCountByTab] = useState<
    Record<ManagerCourseTab, number>
  >({
    enrolled: 0,
    all: 0,
  });
  const [managerLoadedCountByTab, setManagerLoadedCountByTab] = useState<
    Record<ManagerCourseTab, number>
  >({
    enrolled: 0,
    all: 0,
  });
  const [isLoadingManagerCourses, setIsLoadingManagerCourses] = useState(false);
  const [isLoadingMoreManagerCourses, setIsLoadingMoreManagerCourses] =
    useState(false);
  const [bookNowOpen, setBookNowOpen] = useState(false);
  const { handleError, errorModal } = useErrorModal();
  const activeRequestId = useRef(0);

  const subdomain = window.location.hostname.split(".")[0];
  const shouldBlockLms = shouldBlockLmsForSubdomain({
    subdomain,
    permissions: loginState.permissions,
  });
  const effectiveMode = getEffectiveLmsMode({
    subdomain,
    permissions: loginState.permissions,
  });
  const canCreateCourse = loginState.permissions.includes(
    COURSE_CREATE_PERMISSION
  );
  const canUpdateCourse = loginState.permissions.includes(
    COURSE_UPDATE_PERMISSION
  );
  const canViewEntityTranslation = loginState.permissions.includes(
    "frontend_view_entity_translation"
  );

  if (shouldBlockLms) {
    return <Navigate to="/" replace />;
  }

  if (effectiveMode !== requiredMode) {
    return <Navigate to={getLmsCoursesRoute(effectiveMode)} replace />;
  }

  const isManagerMode = effectiveMode === "manager";

  const activeStatusOptions = useMemo(
    () => [
      { value: "true", label: t("active") },
      { value: "false", label: t("inactive") },
    ],
    [t]
  );

  const languageOptions = useMemo(() => getLmsCourseLanguageOptions(t), [t]);

  const adminTableColumns = useMemo<CrudColumnDef[]>(
    () => [
      ...getRecordMetadataColumns(t, ["id"]),
      {
        header: t("name"),
        accessorKey: "title",
        id: "name",
        customColumnFilterId: "title",
        accessorFn: (row: any) => row.title ?? row.name ?? "-",
      },
      {
        header: t("category"),
        accessorKey: "category.name",
        id: "category",
        customColumnFilterId: "category__id",
        filterVariant: "select",
        filterSelectOptions: categoryOptions,
        setNoFilterParameter: true,
        accessorFn: (row: any) => row.category?.name ?? "-",
      },
      {
        header: t("active_course_language_variants"),
        accessorKey: "available_languages",
        id: "language",
        filterVariant: "select",
        filterSelectOptions: languageOptions,
        setNoFilterParameter: true,
        accessorFn: (row: any) =>
          getAvailableLanguagesLabel(
            row.available_languages,
            t,
            row.selected_language
          ),
      },
      {
        header: t("duration"),
        accessorKey: "duration_in_minutes",
        id: "duration_in_minutes",
        filterVariant: "duration",
        durationFilterApiUnit: "minutes",
        accessorFn: (row: any) => getAvailableDurationsLabel(row),
      },
      {
        header: t("status"),
        accessorKey: "is_active",
        id: "is_active",
        customColumnFilterId: "is_active",
        filterVariant: "select",
        filterSelectOptions: activeStatusOptions,
        setNoFilterParameter: true,
        accessorFn: (row: any) =>
          row.is_active === false
            ? t("inactive")
            : row.is_active === true
            ? t("active")
            : "-",
      },
      {
        header: t("created_on"),
        accessorKey: "created_on",
        id: "created_on",
        filterVariant: "date",
        sortingFn: "datetime",
        Cell: ({ cell }: any) => {
          return <span>{replaceDate(cell.getValue(), true)}</span>;
        },
      },
      {
        header: t("created_by"),
        accessorKey: "created_by.name",
        id: "created_by",
        customColumnFilterId: "created_by_name",
        accessorFn: (row: any) => row.created_by?.name ?? "-",
      },
      {
        header: t("modified_on"),
        accessorKey: "modified_on",
        id: "modified_on",
        filterVariant: "date",
        sortingFn: "datetime",
        Cell: ({ cell }: any) => {
          return <span>{replaceDate(cell.getValue(), true)}</span>;
        },
      },
      {
        header: t("modified_by"),
        accessorKey: "modified_by.name",
        id: "modified_by",
        customColumnFilterId: "modified_by_name",
        accessorFn: (row: any) => row.modified_by?.name ?? "-",
      },
    ],
    [activeStatusOptions, categoryOptions, languageOptions, t]
  );

  const tableAdditionalUrlParameters = useMemo(
    () => ({
      ...getLmsManagerRequestParameters(
        effectiveMode,
        selectedChildCompany.subjectId
      ),
    }),
    [effectiveMode, selectedChildCompany.subjectId]
  );

  const getManagerQueryParameters = (tab: ManagerCourseTab) =>
    getManagerCourseListQueryParameters(
      isMarketingMode ? "all" : tab,
      effectiveMode,
      selectedChildCompany.subjectId,
      {
        categoryId: managerFilters.category?.value,
        language: managerFilters.language?.value,
        searchTerm: appliedSearchTerm,
      }
    );

  const getInactiveManagerTab = (tab: ManagerCourseTab): ManagerCourseTab =>
    tab === "enrolled" ? "all" : "enrolled";

  const loadInactiveManagerTabCount = async (
    activeTab: ManagerCourseTab,
    requestId?: number
  ) => {
    const shouldIgnoreResponse = () =>
      requestId !== undefined && activeRequestId.current !== requestId;

    const inactiveTab = getInactiveManagerTab(activeTab);

    try {
      const response = await loadPaginatedEntity(
        "course",
        {
          ...getManagerQueryParameters(inactiveTab),
          limit: 1,
        },
        0
      );

      if (shouldIgnoreResponse()) {
        return;
      }

      setManagerTotalCountByTab((currentCount) => ({
        ...currentCount,
        [inactiveTab]: response.count,
      }));
    } catch (error) {
      if (shouldIgnoreResponse()) {
        return;
      }
      handleError(error);
    }
  };

  const loadManagerCoursePage = async (
    tab: ManagerCourseTab,
    offset = 0,
    append = false,
    requestId?: number
  ) => {
    const shouldIgnoreResponse = () =>
      requestId !== undefined && activeRequestId.current !== requestId;

    handleError(null);

    try {
      const response = await loadPaginatedEntity(
        "course",
        getManagerQueryParameters(tab),
        offset
      );

      if (shouldIgnoreResponse()) {
        return;
      }

      const normalizedCourses = response.results.map((course: any) =>
        mapManagerCourseToCardData(
          course,
          t,
          selectedChildCompany.subjectId !== undefined &&
            selectedChildCompany.subjectId !== null
            ? String(selectedChildCompany.subjectId)
            : undefined
        )
      );

      setManagerCoursesByTab((currentCourses) => ({
        ...currentCourses,
        [tab]: append
          ? [...currentCourses[tab], ...normalizedCourses]
          : normalizedCourses,
      }));
      setManagerTotalCountByTab((currentCount) => ({
        ...currentCount,
        [tab]: response.count,
      }));
      setManagerLoadedCountByTab((currentLoadedCount) => ({
        ...currentLoadedCount,
        [tab]: append
          ? currentLoadedCount[tab] + response.results.length
          : response.results.length,
      }));

      return response.count;
    } catch (error) {
      if (shouldIgnoreResponse()) {
        return;
      }
      handleError(error);
    }
  };

  useEffect(() => {
    if (!isManagerMode) {
      return;
    }

    const requestId = activeRequestId.current + 1;
    activeRequestId.current = requestId;

    const loadCourses = async () => {
      setIsLoadingManagerCourses(true);
      const visibleTab = isMarketingMode ? "all" : managerTab;
      const activeTabCount = await loadManagerCoursePage(
        visibleTab,
        0,
        false,
        requestId
      );

      if (!isMarketingMode) {
        await loadInactiveManagerTabCount(managerTab, requestId);
      }

      if (activeRequestId.current !== requestId) {
        return;
      }

      const shouldDefaultToAllCourses =
        !isMarketingMode &&
        managerTab === "enrolled" &&
        activeTabCount === 0 &&
        !appliedSearchTerm &&
        !managerFilters.category &&
        !managerFilters.language &&
        !hasUserSelectedManagerTab.current;

      if (shouldDefaultToAllCourses) {
        setManagerTab("all");
        return;
      }

      setIsLoadingManagerCourses(false);
    };

    loadCourses();
    return () => {
      activeRequestId.current = requestId;
    };
  }, [
    appliedSearchTerm,
    isManagerMode,
    isMarketingMode,
    managerFilters,
    managerTab,
  ]);

  useEffect(() => {
    const shouldNotImpersonate =
      getLmsManagerRequestConfig(effectiveMode, selectedChildCompany.subjectId)
        .shouldNotImpersonate ?? true;

    loadLmsCourseCategoryOptions({
      shouldNotImpersonate,
      parametersToRender: getLmsManagerRequestParameters(
        effectiveMode,
        selectedChildCompany.subjectId
      ),
    })
      .then((options) =>
        setCategoryOptions(
          options.map((option) => ({
            ...option,
            value: String(option.value),
          }))
        )
      )
      .catch(() => {
        setCategoryOptions([]);
      });
  }, [effectiveMode, selectedChildCompany.subjectId]);

  const visibleManagerTab: ManagerCourseTab = isMarketingMode
    ? "all"
    : managerTab;
  const managerVisibleCourses = managerCoursesByTab[visibleManagerTab];

  const managerActiveFilterCount = useMemo(
    () =>
      [managerFilters.category, managerFilters.language].filter(Boolean).length,
    [managerFilters]
  );

  const emptyStateCopy = useMemo(() => {
    if (appliedSearchTerm) {
      return {
        title: t("no_results_found"),
        message: t("no_courses_found_for_search_term", {
          searchTerm: appliedSearchTerm,
        }),
      };
    }

    if (managerActiveFilterCount > 0) {
      return {
        title: t("no_results_found"),
        message: t("no_results_for_selected_filter"),
      };
    }

    return {
      title: t("no_results_found"),
      message: t("no_courses_found"),
    };
  }, [appliedSearchTerm, managerActiveFilterCount, t]);

  const managerLoadedCount = managerLoadedCountByTab[visibleManagerTab];
  const managerTotalCount = managerTotalCountByTab[visibleManagerTab];
  const managerHasMoreCourses = managerLoadedCount < managerTotalCount;

  const handleLoadMoreManagerCourses = async () => {
    if (isLoadingMoreManagerCourses || !managerHasMoreCourses) {
      return;
    }

    setIsLoadingMoreManagerCourses(true);
    await loadManagerCoursePage(visibleManagerTab, managerLoadedCount, true);
    setIsLoadingMoreManagerCourses(false);
  };

  const handleManagerFilterChange = (
    filterKey: keyof ManagerFilters,
    option: FilterOption | null
  ) => {
    setManagerFilters((currentFilters) => ({
      ...currentFilters,
      [filterKey]: option,
    }));
  };

  const handleResetManagerFilters = () => {
    setManagerFilters(initialManagerFilters);
  };

  const handleOpenCourseOverview = (courseId: string) => {
    navigate(getLmsCourseOverviewRoute(effectiveMode, courseId));
  };

  const handleOpenCourseTranslation = (courseId: string) => {
    navigate(`/course/${courseId}/translation`);
  };

  const handleOpenCreateCourse = () => {
    if (!canCreateCourse) {
      return;
    }

    navigate(getLmsCreateCourseRoute("administrator"));
  };

  const courseRowActions =
    canViewEntityTranslation || !canUpdateCourse
      ? (row: any) => (
          <>
            {canViewEntityTranslation && (
              <Restricted permissionRequired="frontend_view_entity_translation">
                <Tooltip
                  arrow
                  placement="top"
                  enterDelay={1000}
                  enterNextDelay={250}
                  title={t("translation_edit")}
                >
                  <span>
                    <GenericButton
                      variant="icon"
                      onClick={() =>
                        handleOpenCourseTranslation(String(row.original.id))
                      }
                      data-testid="lms-admin-course-translation-button"
                    >
                      <LanguageIcon />
                    </GenericButton>
                  </span>
                </Tooltip>
              </Restricted>
            )}
            {!canUpdateCourse && (
              <Tooltip
                arrow
                placement="top"
                enterDelay={1000}
                enterNextDelay={250}
                title={t("view_course")}
              >
                <span>
                  <GenericButton
                    variant="icon"
                    color="iconBase"
                    onClick={() =>
                      handleOpenCourseOverview(String(row.original.id))
                    }
                    data-testid="lms-admin-course-view-button"
                  >
                    <VisibilityOutlinedIcon />
                  </GenericButton>
                </span>
              </Tooltip>
            )}
          </>
        )
      : undefined;

  if (!isManagerMode) {
    return (
      <GenericCrudTable
        key={`kaer-learning-courses-table-${effectiveMode}`}
        dataTestId="lms-admin-courses-table"
        entity="course"
        columns={adminTableColumns}
        additionalUrlParameter={{
          ...tableAdditionalUrlParameters,
          prefer_translation: "true",
        }}
        additionalKeyForParameteresToRender="title__icontains"
        allowColumnFiltering={true}
        showColumnFilterbyDefault={true}
        columnMinSize={40}
        customCreateHandler={handleOpenCreateCourse}
        disableAddItem={!canCreateCourse}
        customRowActionColSize={canViewEntityTranslation ? 100 : 60}
        customColumnVisibility={{
          category: false,
          created_on: false,
          is_active: false,
        }}
        initialColumnFilters={[{ id: "is_active", value: "true" }]}
        customEditHandler={(row) =>
          handleOpenCourseOverview(String(row.original.id))
        }
        hideEditButtonForRows={!canUpdateCourse}
        hideDeleteButtonForRows
        customRowActions={courseRowActions}
      />
    );
  }

  return (
    <>
      {isMarketingMode && (
        <Grid item xs={12}>
          <LmsMarketingBanner onBookNow={() => setBookNowOpen(true)} />
        </Grid>
      )}
      <Grid item xs={12} className={styles.managerTabsContainer}>
        <RoundedContentBox>
          {!isMarketingMode && (
            <Tabs
              value={managerTab}
              onChange={(_, newValue) => {
                hasUserSelectedManagerTab.current = true;
                setManagerTab(newValue as ManagerCourseTab);
              }}
              className={styles.managerTabs}
              sx={{
                "& .MuiTabs-indicator": { backgroundColor: "#8c1ec8" },
                "& .MuiTab-root.Mui-selected": { color: "#8c1ec8" },
              }}
            >
              <Tab
                data-testid="lms-manager-courses-enrolled-tab"
                value="enrolled"
                label={
                  <span className={styles.tabLabelWithCount}>
                    {t("manager_courses_with_enrolments")}
                    <span className={styles.tabCount}>
                      {managerTotalCountByTab.enrolled}
                    </span>
                  </span>
                }
                className={styles.managerTabPrimary}
              />
              <Tab
                data-testid="lms-manager-courses-all-tab"
                value="all"
                label={
                  <span className={styles.tabLabelWithCount}>
                    {t("all_courses")}
                    <span className={styles.tabCount}>
                      {managerTotalCountByTab.all}
                    </span>
                  </span>
                }
                className={styles.managerTabSecondary}
              />
            </Tabs>
          )}

          <div className={styles.searchFilterContainer}>
            <div
              className={styles.searchFilterInput}
              data-testid="lms-manager-course-search"
            >
              <DefaultInput
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder={t("course_title")}
                style={{ width: "100%" }}
                inputStyle={{
                  boxSizing: "border-box",
                  height: "56px",
                  paddingLeft: "2.5rem",
                  paddingRight: searchInput ? "2.5rem" : "0.75rem",
                  width: "100%",
                }}
              />
              <SearchOutlinedIcon className={styles.searchStartIcon} />
              {searchInput && (
                <ClearIcon
                  className={styles.searchClearIcon}
                  onClick={() => setSearchInput("")}
                />
              )}
            </div>
            <Badge
              badgeContent={managerActiveFilterCount}
              color="secondary"
              invisible={managerActiveFilterCount === 0}
              overlap="rectangular"
            >
              <GenericButton
                variant="outlined"
                color="tertiary"
                onClick={() =>
                  setIsFiltersOpen((currentState) => !currentState)
                }
                startIcon={<TuneOutlinedIcon />}
              >
                {t("more_filters")}
              </GenericButton>
            </Badge>
          </div>

          <Collapse in={isFiltersOpen} timeout={250}>
            <div className={styles.filtersCollapseContainer}>
              <div className={styles.filtersGridManager}>
                <DropDown
                  options={categoryOptions}
                  onChange={(option: any) =>
                    handleManagerFilterChange("category", option)
                  }
                  externalValue={managerFilters.category}
                  isClearable={true}
                  placeholder={t("category")}
                  name="manager-course-category"
                />
                <DropDown
                  options={languageOptions}
                  onChange={(option: any) =>
                    handleManagerFilterChange("language", option)
                  }
                  externalValue={managerFilters.language}
                  isClearable={true}
                  placeholder={t("language")}
                  name="manager-language"
                />
              </div>
              <div className={styles.resetFiltersContainer}>
                <GenericButton
                  onClick={handleResetManagerFilters}
                  startIcon={<ClearIcon />}
                  disabled={managerActiveFilterCount === 0}
                >
                  {t("reset_filters")}
                </GenericButton>
              </div>
            </div>
          </Collapse>
        </RoundedContentBox>
      </Grid>

      <Grid item xs={12}>
        {isLoadingManagerCourses ? (
          <RoundedContentBox className={styles.loadingContainer}>
            <CircularProgress color="secondary" />
          </RoundedContentBox>
        ) : managerVisibleCourses.length === 0 ? (
          <RoundedContentBox className={styles.emptyCoursesContainer}>
            <h3 className={styles.emptyCoursesTitle}>{emptyStateCopy.title}</h3>
            <HeartBrokenIcon
              color="secondary"
              className={styles.noResultsIcon}
              data-testid="lms-manager-no-results-icon"
            />
            {emptyStateCopy.message && (
              <p className={styles.emptyCoursesMessage}>
                {emptyStateCopy.message}
              </p>
            )}
          </RoundedContentBox>
        ) : (
          <>
            <Grid container spacing={"1rem"}>
              {managerVisibleCourses.map((course) => (
                <Grid item xs={12} md={6} lg={4} key={course.id}>
                  <LmsCourseCard
                    course={course}
                    onClick={() =>
                      handleOpenCourseOverview(course.courseId ?? course.id)
                    }
                  />
                </Grid>
              ))}
            </Grid>
            <div className={styles.loadMoreContainer}>
              <span className={styles.loadMoreText}>
                {t("showing_courses", {
                  shown: managerLoadedCount,
                  total: managerTotalCount,
                })}
              </span>
              {managerHasMoreCourses && (
                <>
                  <span className={styles.loadMoreSeparator}>|</span>
                  <button
                    type="button"
                    onClick={handleLoadMoreManagerCourses}
                    className={styles.loadMoreButton}
                    disabled={isLoadingMoreManagerCourses}
                  >
                    {isLoadingMoreManagerCourses ? t("loading") : t("load_more")}
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </Grid>

      <LmsBookNowModal
        open={bookNowOpen}
        onClose={() => setBookNowOpen(false)}
      />
      {errorModal}
    </>
  );
}
